import type { AnalyticsDestination } from "./analytics-destination";

/**
 * The analytics id for a CMS-authored element: the element's own hardcoded id,
 * suffixed with its Sanity array-item `_key` so each authored item is distinct.
 *
 * `analytics/id-must-be-static` normally requires a literal, because a
 * content-managed id is usually translated copy and would fork the identifier
 * once per locale. A `_key` is the exception: it is assigned per array item and
 * carries across a document's translations. The rule cannot tell one runtime
 * string from another, so it recognises this call rather than a property name —
 * every exemption is then a grep away, and the hardcoded part is still checked.
 */
declare const sanityItemKey: unique symbol;

/**
 * A Sanity array-item `_key`. Declaring the field with this type is what makes
 * the key distinguishable from any other string: the lint rule can check that
 * the exemption is the imported helper, but only the type checker knows whether
 * what is handed to it is a key or the rendered copy next to it.
 */
export type SanityItemKey = string & { readonly [sanityItemKey]: never };

export const cmsAnalyticsId = (
  key: SanityItemKey | undefined,
  analyticsId: string,
  destination?: AnalyticsDestination,
): string => {
  const base = destination ? `${analyticsId}_dest_${destination}` : analyticsId;
  return key ? `${base}_cms_${key}` : `${base}_cms_default_unlabelled_id`;
};
